/**
 * Вход балльной оценки из того, что есть в калькуляторе (WS 15587410, п.3).
 *
 * Юнит-экономика — «с учётом всех затрат (без бюджета на запуск)», те же числа, что
 * в первом ряду «Сводки» и в липкой полосе. Вес — вес единицы. Нишевые критерии — из
 * снимка привязанной ниши; лидер ниши (отзывы и рейтинг для критериев 10–11) — товар с
 * наибольшей долей кликов, как в пайплайне. Ниши нет — нишевых критериев нет, и оценка
 * честно предварительная, а не «ниша на нуле».
 */

import type { Niche } from '../../shared/api/client';
import type { CalculatorData } from '../calculator/Calculator';
import { computeUnitEconomics } from '../../shared/lib/unitEconomics';
import { evaluateGoNoGoScore, type GoNoGoScore } from '../../shared/lib/scoring/goNogo';
import { buildNicheCriteria, type NicheDataInput } from '../../shared/lib/scoring/nicheCriteria';
import type { DifferentiationLevel, FragilityLevel } from '../../shared/lib/scoring/constants';

export interface ManualCriteria {
  fragility: FragilityLevel | null;
  differentiation: DifferentiationLevel | null;
}

export function nicheToCriteriaInput(niche: Niche): NicheDataInput {
  const leader = [...niche.top_products]
    .filter((p) => p.click_share != null)
    .sort((a, b) => (b.click_share ?? 0) - (a.click_share ?? 0))[0];
  return {
    searchVolumeT90: niche.search_volume_t90 ?? null,
    searchConversionRate: niche.search_conversion_rate,
    searchVolumeGrowthT90: niche.search_volume_growth_t90 ?? null,
    searchVolumeGrowthT360: niche.search_volume_growth_t360 ?? null,
    maximumUnitsSoldT360: niche.maximum_units_sold_t360 ?? null,
    avgPrice: niche.avg_price,
    productCount: niche.product_count,
    returnRateT360: niche.return_rate,
    top5ClickShare: niche.top5_click_share,
    mainCompetitorReviews: leader?.reviews ?? null,
    mainCompetitorRating: leader?.rating ?? null,
  };
}

export function scoreCalculation(
  data: CalculatorData | undefined,
  launchBudget: number,
  manual: ManualCriteria,
): GoNoGoScore {
  const econ = data ? computeUnitEconomics(data, launchBudget, data.couponPromoEnabled ?? false) : null;
  const finite = (v: number | undefined) => (v !== undefined && Number.isFinite(v) ? v : null);
  return evaluateGoNoGoScore({
    profitPerUnit: econ ? finite(econ.profitPerUnitWithCosts) : null,
    roi: econ ? finite(econ.roiPerUnitWithCosts) : null,
    margin: econ ? finite(econ.marginPerUnitWithCosts) : null,
    weightKg: data && data.unitWeightKg > 0 ? data.unitWeightKg : null,
    fragility: manual.fragility,
    differentiation: manual.differentiation,
    nicheCriteria: data?.niche ? buildNicheCriteria(nicheToCriteriaInput(data.niche)) : null,
  });
}
